import { DEFAULT_LOMDA_TEMPLATE } from "./constants";
import type { LomdaScene, LomdaSceneMedia, LomdaStyle } from "./lomdaTypes";
import { getAssetMediaType, isVideoUrl } from "./mediaAssetUtils";
import { templateHasMediaSlot } from "./sceneTemplates";
import { DEFAULT_LOMDA_STYLE, buildSceneStyleUpdate } from "./styleUtils";

/**
 * Ensures each scene has a valid colors.primary. Only applies the default when
 * the scene has no primary set (null/undefined/empty). Preserves saved
 * backgrounds (images, gradients, colors) from the database.
 */
export function applyBackgroundToAllScenes(scenes: LomdaScene[]): LomdaScene[] {
    return scenes.map((scene) => {
        const bg = scene.style?.background;
        const hasValidBg = bg != null && String(bg).trim() !== "";
        return {
            ...scene,
            style: {
                ...(scene.style ?? {}),
                background: hasValidBg ? bg : DEFAULT_LOMDA_STYLE.background,
            },
        };
    });
}


/** Derive a `LomdaSceneMedia` value from a fileUrl + optional MIME type. */
export function buildSceneMediaItem(
    fileUrl: string,
    fileType?: string
): LomdaSceneMedia {
    const mediaType = fileType
        ? getAssetMediaType(fileType)
        : isVideoUrl(fileUrl)
          ? "video"
          : "image";
    const type = mediaType === "video" ? "video" : "image";
    return { type, url: fileUrl };
}

/**
 * Build the scene update payload for setting a media asset as the scene's
 * layout media slot. Templates with a media slot (B/C/D/E) write to
 * `mediaItem`; templates without a media slot (A/F) treat the asset as a
 * full-scene background and reset style to defaults.
 */
export function buildSceneMediaUpdate(
    scene: LomdaScene,
    fileUrl: string,
    metadataStyle: LomdaStyle | undefined,
    fileType?: string
): Partial<LomdaScene> {
    const isMediaSlotTemplate = templateHasMediaSlot(
        scene.template ?? DEFAULT_LOMDA_TEMPLATE
    );
    if (isMediaSlotTemplate) {
        return { mediaItem: buildSceneMediaItem(fileUrl, fileType) };
    }
    return {
        imageUrl: fileUrl,
        style: buildSceneStyleUpdate(scene.style, metadataStyle, {}),
    };
}
